import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/features/auth/context/auth-context";
import type { ModuleId } from "@/hooks/use-navigation";
import { foldedIncludes } from "@/lib/fold-search-text";
import type { ModuleDef } from "@/lib/module-definitions";
import { getNavModuleLabel } from "@/lib/module-labels";
import { canAccessNavModule } from "@/lib/navigation-permissions";
import {
  modules,
  platformModules,
  SUBTENANT_HIDDEN_SUBMODULES,
} from "@/lib/module-definitions";
import { cn } from "@/lib/utils";
import { ChevronDown, Folder, Search } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { NavUser } from "./nav-user";

interface AppSidebarProps {
  currentModule: string;
  onModuleChange: (module: ModuleId) => void;
}

export function AppSidebar({ currentModule, onModuleChange }: AppSidebarProps) {
  const navigate = useNavigate();
  const { hasPermission, isSystemMode, isTenantMode, permissionsLoading } = useAuth();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const [expandedModules, setExpandedModules] = React.useState<Set<string>>(
    () => {
      try {
        const stored = localStorage.getItem("sidebarExpandedModules");
        if (stored) return new Set(JSON.parse(stored));
      } catch {
        /* ignore */
      }
      return new Set(["mm"]);
    },
  );
  const [expandedMasterGroups, setExpandedMasterGroups] = React.useState<
    Set<string>
  >(() => {
    try {
      const stored = localStorage.getItem("sidebarExpandedMasterGroups");
      if (stored) return new Set(JSON.parse(stored));
    } catch {
      /* ignore */
    }
    return new Set();
  });
  const [searchTerm, setSearchTerm] = React.useState("");
  const isCollapsed = state === "collapsed";

  React.useEffect(() => {
    localStorage.setItem(
      "sidebarExpandedModules",
      JSON.stringify([...expandedModules]),
    );
  }, [expandedModules]);

  React.useEffect(() => {
    localStorage.setItem(
      "sidebarExpandedMasterGroups",
      JSON.stringify([...expandedMasterGroups]),
    );
  }, [expandedMasterGroups]);

  React.useEffect(() => {
    let sourceForExpand = isSystemMode ? platformModules : modules;
    if (!isSystemMode && !isTenantMode) {
      sourceForExpand = sourceForExpand.filter((m) => m.id !== "users-section");
    }
    const parentModule = sourceForExpand.find((mod) => {
      if (mod.submodules?.some((sub) => sub.id === currentModule)) return true;
      if (
        mod.masterGroups?.some((group) =>
          group.submodules.some((sub) => sub.id === currentModule),
        )
      )
        return true;
      return false;
    });
    if (parentModule) {
      setExpandedModules((prev) => new Set([...prev, parentModule.id]));
      if (parentModule.masterGroups) {
        for (const group of parentModule.masterGroups) {
          if (group.submodules.some((sub) => sub.id === currentModule)) {
            setExpandedMasterGroups((prev) => new Set([...prev, group.id]));
            break;
          }
        }
      }
    }
  }, [currentModule, isSystemMode, isTenantMode]);

  const hasNavigationAccess = React.useCallback(
    (moduleId: string): boolean =>
      canAccessNavModule(moduleId, hasPermission, permissionsLoading),
    [hasPermission, permissionsLoading],
  );

  const resolveModuleLabel = React.useCallback(
    (moduleId: string, fallbackLabel: string) =>
      getNavModuleLabel(moduleId, fallbackLabel),
    [],
  );

  const permissionFilteredModules = React.useMemo(() => {
    const sourceModules = isSystemMode ? platformModules : modules;
    const isSubtenantMode = !isSystemMode && !isTenantMode;
    return sourceModules
      .map((module) => {
        let hasVisible = false;
        let next: ModuleDef = { ...module };

        if (module.masterGroups) {
          const visibleMasterGroups = module.masterGroups
            .map((group) => {
              let subs = group.submodules;
              if (isSubtenantMode) {
                const hidden = SUBTENANT_HIDDEN_SUBMODULES[module.id];
                if (hidden) {
                  subs = subs.filter((sub) => !hidden.includes(sub.id));
                }
              }
              return {
                ...group,
                submodules: subs.filter((sub) => hasNavigationAccess(sub.id)),
              };
            })
            .filter((group) => group.submodules.length > 0);
          if (visibleMasterGroups.length > 0) {
            next = { ...next, masterGroups: visibleMasterGroups };
            hasVisible = true;
          } else {
            next = { ...next, masterGroups: undefined };
          }
        }

        if (module.submodules) {
          let subs = module.submodules;
          if (isSubtenantMode) {
            const hidden = SUBTENANT_HIDDEN_SUBMODULES[module.id];
            if (hidden) {
              subs = subs.filter((sub) => !hidden.includes(sub.id));
            }
          }
          const visibleSubmodules = subs.filter((sub) =>
            hasNavigationAccess(sub.id),
          );
          if (visibleSubmodules.length > 0) {
            next = { ...next, submodules: visibleSubmodules };
            hasVisible = true;
          } else {
            next = { ...next, submodules: undefined };
          }
        }

        if (module.hasSubmodules) {
          return hasVisible ? next : null;
        }
        return hasNavigationAccess(module.id) ? module : null;
      })
      .filter(
        (module): module is ModuleDef =>
          module !== null &&
          (module.hasSubmodules || hasNavigationAccess(module.id)),
      );
  }, [hasNavigationAccess, isSystemMode, isTenantMode]);

  const filteredModules = React.useMemo(() => {
    if (!searchTerm.trim()) return permissionFilteredModules;
    return permissionFilteredModules.filter((module) => {
      const moduleLabel = resolveModuleLabel(module.id, module.name);
      if (foldedIncludes(moduleLabel, searchTerm)) return true;
      if (
        module.submodules?.some((sub) =>
          foldedIncludes(resolveModuleLabel(sub.id, sub.name), searchTerm),
        )
      )
        return true;
      if (module.masterGroups) {
        return module.masterGroups.some((group) => {
          if (foldedIncludes(group.name, searchTerm)) return true;
          return group.submodules.some((sub) =>
            foldedIncludes(resolveModuleLabel(sub.id, sub.name), searchTerm),
          );
        });
      }
      return false;
    });
  }, [permissionFilteredModules, searchTerm, resolveModuleLabel]);

  React.useEffect(() => {
    if (!searchTerm.trim() || filteredModules.length === 0) return;

    const modulesToExpand = new Set<string>();
    const masterGroupsToExpand = new Set<string>();

    for (const module of filteredModules) {
      const moduleLabel = resolveModuleLabel(module.id, module.name);
      const moduleMatches = foldedIncludes(moduleLabel, searchTerm);

      if (module.masterGroups) {
        let groupMatched = false;
        for (const group of module.masterGroups) {
          const groupMatches =
            foldedIncludes(group.name, searchTerm) ||
            group.submodules.some((sub) =>
              foldedIncludes(resolveModuleLabel(sub.id, sub.name), searchTerm),
            );
          if (groupMatches) {
            modulesToExpand.add(module.id);
            masterGroupsToExpand.add(group.id);
            groupMatched = true;
          }
        }
        if (moduleMatches && !groupMatched) {
          modulesToExpand.add(module.id);
        }
      } else if (module.submodules) {
        const subMatches = module.submodules.some((sub) =>
          foldedIncludes(resolveModuleLabel(sub.id, sub.name), searchTerm),
        );
        if (moduleMatches || subMatches) {
          modulesToExpand.add(module.id);
        }
      }
    }

    if (modulesToExpand.size > 0) {
      setExpandedModules((prev) => new Set([...prev, ...modulesToExpand]));
    }
    if (masterGroupsToExpand.size > 0) {
      setExpandedMasterGroups(
        (prev) => new Set([...prev, ...masterGroupsToExpand]),
      );
    }
  }, [searchTerm, filteredModules, resolveModuleLabel]);

  const isModuleExpanded = (moduleId: string) => expandedModules.has(moduleId);
  const isMasterGroupExpanded = (groupId: string) =>
    expandedMasterGroups.has(groupId);

  const handleClick = (id: string) => {
    if (id === "") {
      navigate("/");
    } else {
      onModuleChange(id as ModuleId);
    }
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader className="h-[56.5px] items-center content-center border-b border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="hover:bg-muted/50 data-[active=true]:bg-transparent gap-0"
              tooltip="Inicio"
            >
              <button
                onClick={() => handleClick("")}
                className="flex items-center gap-2"
              >
                {isCollapsed ? (
                  <img src="/logo.png" alt="UnifiK" className="h-8 w-8" />
                ) : (
                  <img
                    src="/logo-ext.png"
                    alt="UnifiK"
                    className="h-8 w-auto"
                  />
                )}
              </button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Search */}
        {!isCollapsed && (
          <div className="px-2 pt-1">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-sidebar-foreground/50" />
              <SidebarInput
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-7 pl-7 text-xs border border-sidebar-border bg-sidebar-accent text-sidebar-foreground shadow-none placeholder:text-sidebar-foreground/50 focus-visible:ring-sidebar-ring"
              />
            </div>
          </div>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>Módulos</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredModules.map((module) => {
                const Icon = module.icon;
                const containsCurrent =
                  currentModule === module.id ||
                  module.masterGroups?.some((g) =>
                    g.submodules.some((s) => s.id === currentModule),
                  ) ||
                  module.submodules?.some((s) => s.id === currentModule);

                const isExpanded = isModuleExpanded(module.id);
                // Solo está activo si no tiene hijos o si sus hijos están ocultos (colapsado)
                const isActive =
                  containsCurrent && (!module.hasSubmodules || !isExpanded);

                if (!module.hasSubmodules) {
                  return (
                    <SidebarMenuItem key={module.id}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => handleClick(module.id)}
                        tooltip={resolveModuleLabel(module.id, module.name)}
                        className="sidebar-item-active-parent"
                      >
                        <div
                          className={cn(
                            "w-5 h-5 rounded flex items-center justify-center shrink-0",
                            module.iconBadgeClass,
                          )}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <span>
                          {resolveModuleLabel(module.id, module.name)}
                        </span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                // Icon-rail only: Sheet/mobile keeps Collapsible expand (old behavior).
                if (isCollapsed && !isMobile) {
                  const moduleLabel = resolveModuleLabel(
                    module.id,
                    module.name,
                  );
                  return (
                    <SidebarMenuItem key={module.id}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <SidebarMenuButton
                            isActive={containsCurrent}
                            className="sidebar-item-active-parent"
                            aria-label={moduleLabel}
                          >
                            <div
                              className={cn(
                                "w-5 h-5 rounded flex items-center justify-center shrink-0",
                                module.iconBadgeClass,
                              )}
                            >
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <span>{moduleLabel}</span>
                          </SidebarMenuButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side="right"
                          align="start"
                          sideOffset={8}
                          className="min-w-52"
                        >
                          <DropdownMenuLabel>{moduleLabel}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {module.submodules?.map((sub) => {
                            const SubIcon = sub.icon;
                            return (
                              <DropdownMenuItem
                                key={sub.id}
                                onSelect={() =>
                                  handleClick(sub.id)
                                }
                                className={cn(
                                  currentModule === sub.id && "bg-accent",
                                )}
                              >
                                <SubIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                {resolveModuleLabel(sub.id, sub.name)}
                              </DropdownMenuItem>
                            );
                          })}
                          {module.masterGroups?.map((group, groupIndex) => (
                            <React.Fragment key={group.id}>
                              {(groupIndex > 0 ||
                                (module.submodules?.length ?? 0) > 0) && (
                                <DropdownMenuSeparator />
                              )}
                              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                                {group.name}
                              </DropdownMenuLabel>
                              {group.submodules.map((sub) => {
                                const SubIcon = sub.icon;
                                return (
                                  <DropdownMenuItem
                                    key={sub.id}
                                    onSelect={() =>
                                      handleClick(sub.id)
                                    }
                                    className={cn(
                                      currentModule === sub.id && "bg-accent",
                                    )}
                                  >
                                    <SubIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                    {resolveModuleLabel(sub.id, sub.name)}
                                  </DropdownMenuItem>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <Collapsible
                    key={module.id}
                    open={isExpanded}
                    onOpenChange={() => {
                      setExpandedModules((prev) => {
                        const next = new Set(prev);
                        next.has(module.id)
                          ? next.delete(module.id)
                          : next.add(module.id);
                        return next;
                      });
                    }}
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip={resolveModuleLabel(module.id, module.name)}
                        >
                          <div
                            className={cn(
                              "w-5 h-5 rounded flex items-center justify-center shrink-0",
                              module.iconBadgeClass,
                            )}
                          >
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <span>
                            {resolveModuleLabel(module.id, module.name)}
                          </span>
                          <ChevronDown
                            className={cn(
                              "ml-auto h-3 w-3 transition-transform duration-200",
                              isExpanded && "rotate-180",
                            )}
                          />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                    </SidebarMenuItem>

                    <CollapsibleContent>
                      {/* Regular submodules (transaccionales u planos) */}
                      {module.submodules && (
                        <SidebarMenuSub>
                          {module.submodules
                            .filter((sub) => {
                              if (!searchTerm.trim()) return true;
                              return foldedIncludes(
                                resolveModuleLabel(sub.id, sub.name),
                                searchTerm,
                              );
                            })
                            .map((sub) => {
                              const SubIcon = sub.icon;
                              const isSubActive = currentModule === sub.id;
                              return (
                                <SidebarMenuItem key={sub.id}>
                                  <SidebarMenuSubButton
                                    isActive={isSubActive}
                                    onClick={() =>
                                      handleClick(sub.id)
                                    }
                                    className="min-h-7 h-auto"
                                  >
                                    <SubIcon
                                      className="h-3 w-3 text-gray-500 shrink-0"
                                      strokeWidth={1.5}
                                    />
                                    <span>
                                      {resolveModuleLabel(sub.id, sub.name)}
                                    </span>
                                  </SidebarMenuSubButton>
                                </SidebarMenuItem>
                              );
                            })}
                        </SidebarMenuSub>
                      )}

                      {/* Master groups */}
                      {module.masterGroups && (
                        <SidebarMenuSub>
                          {module.masterGroups
                            .filter((group) => {
                              if (!searchTerm.trim()) return true;
                              return (
                                foldedIncludes(group.name, searchTerm) ||
                                group.submodules.some((sub) =>
                                  foldedIncludes(
                                    resolveModuleLabel(sub.id, sub.name),
                                    searchTerm,
                                  ),
                                )
                              );
                            })
                            .map((group) => {
                              const containsCurrentSubmodule =
                                group.submodules.some(
                                  (s) => s.id === currentModule,
                                );
                              const isGroupExpanded = isMasterGroupExpanded(
                                group.id,
                              );
                              // Activo solo si contiene el módulo actual y está colapsado
                              const isGroupActive =
                                containsCurrentSubmodule && !isGroupExpanded;

                              return (
                                <Collapsible
                                  key={group.id}
                                  open={isGroupExpanded}
                                  onOpenChange={() => {
                                    setExpandedMasterGroups((prev) => {
                                      const next = new Set(prev);
                                      next.has(group.id)
                                        ? next.delete(group.id)
                                        : next.add(group.id);
                                      return next;
                                    });
                                  }}
                                >
                                  <SidebarMenuItem>
                                    <CollapsibleTrigger asChild>
                                      <SidebarMenuSubButton
                                        isActive={isGroupActive}
                                        className={cn(
                                          "min-h-7 h-auto",
                                          isGroupActive &&
                                            "sidebar-item-active",
                                        )}
                                      >
                                        <Folder className="h-3 w-3 text-muted-foreground shrink-0" />
                                        <span>{group.name}</span>
                                        <ChevronDown
                                          className={cn(
                                            "ml-auto h-3 w-3 transition-transform duration-200 shrink-0",
                                            isGroupExpanded && "rotate-180",
                                          )}
                                        />
                                      </SidebarMenuSubButton>
                                    </CollapsibleTrigger>
                                  </SidebarMenuItem>
                                  <CollapsibleContent>
                                    <SidebarMenuSub>
                                      {group.submodules
                                        .filter((sub) => {
                                          if (!searchTerm.trim()) return true;
                                          return foldedIncludes(
                                            resolveModuleLabel(
                                              sub.id,
                                              sub.name,
                                            ),
                                            searchTerm,
                                          );
                                        })
                                        .map((sub) => {
                                          const SubIcon = sub.icon;
                                          const isSubActive =
                                            currentModule === sub.id;
                                          return (
                                            <SidebarMenuItem key={sub.id}>
                                              <SidebarMenuSubButton
                                                isActive={isSubActive}
                                                onClick={() =>
                                                  handleClick(sub.id)
                                                }
                                                className="min-h-7 h-auto"
                                              >
                                                <SubIcon
                                                  className="h-3 w-3 text-gray-500 shrink-0"
                                                  strokeWidth={1.5}
                                                />
                                                <span>
                                                  {resolveModuleLabel(
                                                    sub.id,
                                                    sub.name,
                                                  )}
                                                </span>
                                              </SidebarMenuSubButton>
                                            </SidebarMenuItem>
                                          );
                                        })}
                                    </SidebarMenuSub>
                                  </CollapsibleContent>
                                </Collapsible>
                              );
                            })}
                        </SidebarMenuSub>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <NavUser />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
